import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { LLCTransaction } from '@models/llc-mortgages';
import { MortgageStep } from '@models/mortgage';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { MatterMortgageDispatch } from '@state/matter/matter_mortgages';
import { LLCMortgageState } from '@state/mortgages/llc-mortgage/llc-mortgage.state';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { Step } from 'src/app/shared/atomic/molecules/step-progress-nav/step-progress-nav.component';

interface LLCExistingMatterModalBase {
	matterMortgageId: string;
}

interface LLCExistingMatterModalViaId extends LLCExistingMatterModalBase {
	transactionId: string;
}

interface LLCExistingMatterModalViaTransaction
	extends LLCExistingMatterModalBase {
	transaction: LLCTransaction;
}

export type LLCExistingMatterModalData =
	| LLCExistingMatterModalViaId
	| LLCExistingMatterModalViaTransaction;

enum StepTypes {
	BORROWERS = 'borrowers',
	SALE_PRICE = 'salePrice',
	SYNC_DATA = 'synData',
}

const StepsWithPurchaseOrder: StepTypes[] = [
	StepTypes.BORROWERS,
	StepTypes.SALE_PRICE,
	StepTypes.SYNC_DATA,
];

const StepsWithoutPurchaseOrder: StepTypes[] = [
	StepTypes.BORROWERS,
	StepTypes.SYNC_DATA,
];

const StepsWithPurchase: Step[] = [
	{
		title: 'Reconcile borrower(s)',
		subTitle: "Match lender's borrower records with your matter",
	},
	{
		title: 'Reconcile purchase price',
		subTitle: "Confirm price on file matches lender's",
	},
	{
		title: 'Sync data',
		subTitle: 'Import other pertinent details from instructions',
	},
];

const StepsWithoutPurchase: Step[] = [
	{
		title: 'Reconcile borrower(s)',
		subTitle: "Match lender's borrower records with your matter",
	},
	{
		title: 'Sync data',
		subTitle: 'Import other pertinent details from instructions',
	},
];

@UntilDestroy()
@Component({
	selector: 'app-existing-matter',
	templateUrl: './existing-matter.component.html',
	standalone: false,
})
export class LLCExistingMatterModalComponent implements OnInit {
	public steps = StepsWithPurchase;
	public allSteps = StepTypes;
	public stepsOrder = StepsWithPurchaseOrder;
	public selectedIndex = 0;
	public selectedStep = StepTypes.BORROWERS;
	public transaction: LLCTransaction;
	public matterMortgageId: string;
	public showPurchaseStep = true;

	@SelectSnapshot(SelectedMatter.id) matterId: string;
	@Select(LLCMortgageState.getLLCMortgage) llcMortgageFilterFn$: Observable<
		(id) => LLCTransaction
	>;

	constructor(
		public dialogRef: MatDialogRef<LLCExistingMatterModalComponent>,
		@Inject(MAT_DIALOG_DATA) public data: LLCExistingMatterModalData,
		private matterMortgageDispatch: MatterMortgageDispatch,
	) {}

	getMortgage$(id: string): Observable<LLCTransaction> {
		return this.llcMortgageFilterFn$.pipe(
			untilDestroyed(this),
			map((fn) => fn(id)),
		);
	}

	ngOnInit(): void {
		this.dialogRef.updateSize('1000px', '900px');
		const { data } = this || {};
		const { matterMortgageId } = data;
		const dataAsId = data as LLCExistingMatterModalViaId;
		const dataAsTransaction = data as LLCExistingMatterModalViaTransaction;

		if (dataAsId?.transactionId) {
			this.getMortgage$(dataAsId.transactionId)
				.pipe(
					filter((x) => !!x?.matterMortgageId),
					untilDestroyed(this),
				)
				.subscribe((newTransactionData) => {
					this.setTransaction(newTransactionData);
				});
		} else if (dataAsTransaction?.transaction) {
			this.setTransaction(dataAsTransaction.transaction);
		}
		this.matterMortgageId = matterMortgageId;
	}

	goToMortgage() {
		this.matterMortgageDispatch.updateMortgage(this.matterMortgageId, {
			step: MortgageStep.CREATED,
		});
		this.dialogRef.close();
	}

	next(): void {
		const next = this.selectedIndex + 1;

		if (next > this.stepsOrder.length - 1) {
			this.goToMortgage();

			return;
		}

		this.selectedIndex = next;
		this.selectedStep = this.stepsOrder[next];
	}

	goTo(index: number) {
		this.selectedIndex = index;
		this.selectedStep = this.stepsOrder[index];
	}

	private setTransaction(transaction: LLCTransaction) {
		this.transaction = transaction;

		if (transaction.data?.mortgage?.purchasePrice) {
			this.steps = StepsWithPurchase;
			this.stepsOrder = StepsWithPurchaseOrder;
			this.showPurchaseStep = true;
		} else {
			this.steps = StepsWithoutPurchase;
			this.stepsOrder = StepsWithoutPurchaseOrder;
			this.showPurchaseStep = false;
		}
	}
}
